import { differenceInYears } from 'date-fns/differenceInYears';
import { isValid } from 'date-fns/isValid';
import { parse } from 'date-fns/parse';
import { useMemo } from 'react';

import {
  MAX_DISALLOWED_BIRTH_YEAR,
  validUSPhoneNumberRegex,
} from '@/constants';
import { useEcomStoreSelector } from '@/data';
import { EnumCustomerType } from '@/types';
import { allowedAge } from '@/utils';

import { useSelectedStore } from './useSelectedStore';

const isFirstNameRequired =
  process?.env?.NEXT_PUBLIC_KIOSK_FIRST_NAME_REQUIRED === 'true';
const isLastNameRequired =
  process?.env?.NEXT_PUBLIC_KIOSK_LAST_NAME_REQUIRED === 'true';
const isEmailRequired =
  process?.env?.NEXT_PUBLIC_KIOSK_EMAIL_REQUIRED === 'true';
const isPhoneRequired =
  process?.env?.NEXT_PUBLIC_KIOSK_PHONE_REQUIRED === 'true';
const isDobRequired = process?.env?.NEXT_PUBLIC_KIOSK_DOB_REQUIRED === 'true';
const hideOptionalContactFields =
  process.env.NEXT_PUBLIC_KIOSK_HIDE_OPTIONAL_CONTACT_FIELDS === 'true';
const isStateOfResidenceRequired =
  process.env.NEXT_PUBLIC_FEATURE_FLAG_REQUIRE_STATE_AT_CHECKOUT === 'true';

const getFieldsValidationRules = (minAge: number) => [
  ...(isFirstNameRequired
    ? [
        {
          field: 'first_name',
          message: 'Enter your first name.',
          method: 'isEmpty',
          validWhen: false,
        },
      ]
    : []),
  {
    args: [{ min: 1 }],
    field: 'first_name',
    message: 'First name must have at least one character',
    method: 'isLength',
    validWhen: true,
  },
  ...(isLastNameRequired
    ? [
        {
          field: 'last_name',
          message: 'Enter your last name.',
          method: 'isEmpty',
          validWhen: false,
        },
      ]
    : []),
  {
    args: [{ min: 1 }],
    field: 'last_name',
    message: 'Last name must have at least one character',
    method: 'isLength',
    validWhen: true,
  },
  ...(isPhoneRequired
    ? [
        {
          field: 'phone_number',
          message: 'Enter your phone number.',
          method: 'isEmpty',
          validWhen: false,
        },
      ]
    : []),
  {
    field: 'phone_number',
    message: 'The phone format is incorrect.',
    method: (phone: string) => {
      return RegExp(/\+1 \(\d{3}\) \d{3}-\d{4}$/).test(phone);
    },
    validWhen: true,
  },
  {
    field: 'phone_number',
    message: 'Phone number must be a valid US number.',
    method: (phone: string) => {
      return RegExp(validUSPhoneNumberRegex).test(phone);
    },
    validWhen: true,
  },
  ...(isEmailRequired
    ? [
        {
          field: 'email',
          message: 'Enter your email.',
          method: 'isEmpty',
          validWhen: false,
        },
      ]
    : []),
  ...(isStateOfResidenceRequired
    ? [
        {
          field: 'state_of_residence',
          message: 'You must select a state of residence.',
          method: 'isEmpty',
          validWhen: false,
        },
      ]
    : []),
  {
    field: 'email',
    message: 'The email format is incorrect.',
    method: 'isEmail',
    validWhen: true,
  },
  {
    field: 'date_of_birth',
    message: 'The birthdate format is incorrect.',
    method: 'isDate',
    validWhen: true,
  },
  ...(isDobRequired
    ? [
        {
          field: 'date_of_birth',
          message: 'Enter your date of birth.',
          method: 'isEmpty',
          validWhen: false,
        },
      ]
    : []),
  {
    field: 'date_of_birth',
    message: `Should be at least ${minAge} years old.`,
    method: current => {
      return differenceInYears(new Date(), new Date(current)) >= minAge;
    },
    validWhen: true,
  },
  {
    field: 'date_of_birth',
    message: `Birth year must be ${MAX_DISALLOWED_BIRTH_YEAR + 1} or later.`,
    method: current => {
      if (!current) {
        return true;
      }

      const parsedDate = parse(current, 'P', new Date());
      if (!isValid(parsedDate)) {
        return true;
      }

      return parsedDate.getFullYear() > MAX_DISALLOWED_BIRTH_YEAR;
    },
    validWhen: true,
  },
  {
    field: 'date_of_birth',
    message: 'Invalid date',
    method: current => {
      if (!current) {
        return true;
      }
      const parsedDate = parse(current, 'P', new Date());
      const isValidDate = isValid(parsedDate);
      return isValidDate;
    },
    validWhen: true,
  },
];

interface IUseKioskAccountFormRulesResponse {
  isFirstNameRequired: boolean;
  isLastNameRequired: boolean;
  isEmailRequired: boolean;
  isPhoneRequired: boolean;
  isDobRequired: boolean;
  isStateOfResidenceRequired: boolean;
  hideOptionalContactFields: boolean;
  formRules: any[];
}

const useKioskAccountFormRules = (): IUseKioskAccountFormRulesResponse => {
  const { store: selectedStore } = useSelectedStore();
  const { customer_type } = useEcomStoreSelector(['customer_type']);

  const minAge = allowedAge(
    customer_type ?? EnumCustomerType.ADULT,
    selectedStore?.age_restriction,
  );

  const formRules = useMemo(() => getFieldsValidationRules(minAge), [minAge]);

  return {
    isFirstNameRequired,
    isLastNameRequired,
    isEmailRequired,
    isPhoneRequired,
    isDobRequired,
    isStateOfResidenceRequired,
    hideOptionalContactFields,
    formRules,
  };
};

export { useKioskAccountFormRules };
